import type { PlanFeatureInput } from '@frameio/graphql-client/generated';
import { useIsEnterpriseSubscriber } from '@frameio/shared/hooks';
import { TooltipDetails, TooltipTitle } from '@frameio/vapor';
import type { FeatureInput } from '../hooks/useFeatureGatePlanSuggestion';
import { useFeatureGatePlanSuggestion } from '../hooks/useFeatureGatePlanSuggestion';
import { useIsPublicShare } from '../hooks/useIsPublicShare';
import { usePlanFeatureInputFromSuggestion } from '../hooks/usePlanFeatureInputFromSuggestion';
import styles from './FeatureGateTooltipTitle.module.css';
import TEXT from './generated/TEXT/FeatureGateTooltipTitle';

TEXT.DEFINE(
  {
    /** Tooltip for Enterprise subscribers viewing add-on features */
    enterpriseAddOn: () => (
      <>
        <TooltipTitle>This feature is an Enterprise add-on.</TooltipTitle>
        <TooltipDetails>Click to see details.</TooltipDetails>
      </>
    ),
    /** Tooltip for Enterprise plan upgrade */
    availableWithEnterprise: () => (
      <>
        <TooltipTitle>
          This feature is only available with an{' '}
          <strong>Enterprise plan</strong>.
        </TooltipTitle>
        <TooltipDetails>Click to upgrade.</TooltipDetails>
      </>
    ),
    /** Tooltip for other plan upgrades (Pro, Team, etc.) */
    availableWithPlan: (planTitle: string) => (
      <>
        <TooltipTitle>
          This feature is only available with a{' '}
          <strong>{planTitle} plan</strong> or above.
        </TooltipTitle>
        <TooltipDetails>Click to upgrade.</TooltipDetails>
      </>
    ),
    /** Tooltip when no plan title (edge case) */
    availableWithNoPlan: () => (
      <>
        <TooltipDetails>Click to upgrade.</TooltipDetails>
      </>
    ),
    /** Tooltip for public share views */
    shareTooltipTitle: (feature: string) => (
      <TooltipTitle>{feature} not available on this account</TooltipTitle>
    ),
    /** Feature display names used in public share view tooltips */
    featureDisplayNames: {
      /** Email domain restrictions for account access */
      allowedDomains: 'Allowed domains are',
      /** Long-term storage for inactive content */
      archivalStorage: 'Archival storage is',
      /** Automated content lifecycle policies */
      assetLifecycleManagement: 'Asset lifecycle management is',
      /** Ability to attach files to comments */
      commentAttachments: 'Comment attachments are',
      /** Custom email branding for notifications */
      customBrandedEmails: 'Custom-branded emails are',
      /** Custom branding for presentation mode */
      customBrandedPresentations: 'Custom-branded presentations are',
      /** Custom branding for share links */
      customBrandedShares: 'Custom-branded shares are',
      /** Custom ordering of assets in projects */
      customSort: 'Custom sort is',
      /** Desktop and mobile device integrations */
      devices: 'Devices are',
      /** Restrict team member invitation permissions */
      disableTeamMemberInvites: 'Disabling team member invites is',
      /** Digital rights management protection */
      drm: 'DRM is',
      /** Unique watermarking for forensic tracking */
      forensicWatermarking: 'Forensic watermarking is',
      /** 4K ultra-high definition video playback */
      fourKPlayback: '4K UHD playback is',
      /** Generic fallback used when a plan feature has no specific display name yet */
      genericFeature: 'This feature is',
      /** High dynamic range video support */
      hdr: 'HDR is',
      /** External storage mounting capability */
      mountedStorage: 'Mounted storage is',
      /** Allow storage usage beyond plan limits */
      mountedStorageOverprovisioningAllowed:
        'Mounted storage overprovisioning is',
      /** Natural language search for content */
      naturalLanguageSearch: 'Natural language search is',
      /** Passphrase protection for share links */
      passwordProtectedShares: 'Passphrase-protected shares are',
      /** Projects with restricted access controls */
      privateProjects: 'Restricted projects are',
      /** Reel-style layout for video review */
      reelPlayer: 'Reel layout is',
      /** Content security policy management */
      resourceControlPolicies: 'Content security settings are',
      /** Folders with restricted permissions */
      restrictedFolders: 'Restricted folders are',
      /** Advanced share link security features */
      secureSharing: 'Secure sharing is',
      /** AI-powered semantic content search */
      semanticSearch: 'Semantic search is',
      /** Session-specific watermarking */
      sessionBasedWatermarking: 'Session-based watermarking is',
      /** Time-limited share link access */
      shareLinkExpiration: 'Share link expiration is',
      /** Share link permission controls */
      shareLinkSecurity: 'Share link security & permissions are',
      /** Share lists feature */
      shareLists: 'Share lists are',
      /** Static watermark overlays */
      staticWatermark: 'Static watermark is',
      /** Connect external storage systems */
      storageConnect: 'Storage connect is',
      /** Internal-only commenting feature */
      teamOnlyComments: 'Internal comments are',
      /** Automatic video transcription */
      transcription: 'Transcription is',
      /** Unlimited custom watermark templates */
      unlimitedWatermarkTemplates: 'Unlimited watermark templates are',
      /** Shareable links for unauthenticated uploads to a folder */
      uploadLinks: 'Upload links are',
      /** User group management feature */
      userGroups: 'User groups are',
      /** Advanced user permission management */
      userManagement: 'User management is',
    },
  },
  { TooltipTitle, TooltipDetails }
);

type FeatureDisplayNames = {
  [key in PlanFeatureInput]?: string;
};

const featureDisplayNames: FeatureDisplayNames = {
  SHARE_LISTS: TEXT.featureDisplayNames.shareLists,
  ALLOWED_DOMAINS: TEXT.featureDisplayNames.allowedDomains,
  ARCHIVAL_STORAGE: TEXT.featureDisplayNames.archivalStorage,
  ASSET_LIFECYCLE_MANAGEMENT: TEXT.featureDisplayNames.assetLifecycleManagement,
  COMMENT_ATTACHMENTS: TEXT.featureDisplayNames.commentAttachments,
  CUSTOM_BRANDED_EMAILS: TEXT.featureDisplayNames.customBrandedEmails,
  CUSTOM_BRANDED_PRESENTATIONS:
    TEXT.featureDisplayNames.customBrandedPresentations,
  CUSTOM_BRANDED_SHARES: TEXT.featureDisplayNames.customBrandedShares,
  CUSTOM_SORT: TEXT.featureDisplayNames.customSort,
  DEVICES: TEXT.featureDisplayNames.devices,
  DISABLE_SHARE_COMMENTS: TEXT.featureDisplayNames.shareLinkSecurity,
  DISABLE_SHARE_DOWNLOADS: TEXT.featureDisplayNames.shareLinkSecurity,
  DISABLE_SHARE_PREVIOUS_VERSIONS: TEXT.featureDisplayNames.shareLinkSecurity,
  DISABLE_TEAM_MEMBER_INVITES:
    TEXT.featureDisplayNames.disableTeamMemberInvites,
  DRM: TEXT.featureDisplayNames.drm,
  FORENSIC_WATERMARKING: TEXT.featureDisplayNames.forensicWatermarking,
  FOUR_K_PLAYBACK: TEXT.featureDisplayNames.fourKPlayback,
  HDR: TEXT.featureDisplayNames.hdr,
  MOUNTED_STORAGE: TEXT.featureDisplayNames.mountedStorage,
  MOUNTED_STORAGE_OVERPROVISIONING_ALLOWED:
    TEXT.featureDisplayNames.mountedStorageOverprovisioningAllowed,
  NATURAL_LANGUAGE_SEARCH: TEXT.featureDisplayNames.naturalLanguageSearch,
  PASSWORD_PROTECTED_SHARES: TEXT.featureDisplayNames.passwordProtectedShares,
  PRIVATE_PROJECTS: TEXT.featureDisplayNames.privateProjects,
  REEL_PLAYER: TEXT.featureDisplayNames.reelPlayer,
  RESOURCE_CONTROL_POLICIES: TEXT.featureDisplayNames.resourceControlPolicies,
  RESTRICTED_FOLDERS: TEXT.featureDisplayNames.restrictedFolders,
  SECURE_SHARING: TEXT.featureDisplayNames.secureSharing,
  SEMANTIC_SEARCH: TEXT.featureDisplayNames.semanticSearch,
  SESSION_BASED_WATERMARKING: TEXT.featureDisplayNames.sessionBasedWatermarking,
  SHARE_LINK_EXPIRATION: TEXT.featureDisplayNames.shareLinkExpiration,
  STATIC_WATERMARK: TEXT.featureDisplayNames.staticWatermark,
  STORAGE_CONNECT: TEXT.featureDisplayNames.storageConnect,
  TEAM_ONLY_COMMENTS: TEXT.featureDisplayNames.teamOnlyComments,
  TRANSCRIPTION: TEXT.featureDisplayNames.transcription,
  UNLIMITED_WATERMARK_TEMPLATES:
    TEXT.featureDisplayNames.unlimitedWatermarkTemplates,
  UPLOAD_LINKS: TEXT.featureDisplayNames.uploadLinks,
  USER_GROUPS: TEXT.featureDisplayNames.userGroups,
  USER_MANAGEMENT: TEXT.featureDisplayNames.userManagement,
};

// Falls back to a generic name so a feature the backend added but the client
// hasn't mapped yet still renders a readable tooltip instead of `undefined`.
const getFeatureDisplayName = (feature: PlanFeatureInput): string =>
  featureDisplayNames[feature] ?? TEXT.featureDisplayNames.genericFeature;

type FeatureGateTooltipTitleProps = {
  feature: FeatureInput;
};

export function FeatureGateTooltipTitle({
  feature,
}: FeatureGateTooltipTitleProps) {
  const isPublicShare = useIsPublicShare();
  const featureGatePlanSuggestion = useFeatureGatePlanSuggestion(feature);
  const isEnterpriseSubscriber = useIsEnterpriseSubscriber();
  const planFeatureInputFromSuggestion =
    usePlanFeatureInputFromSuggestion(feature);

  if (isPublicShare) {
    return (
      <div className={styles.container}>
        {TEXT.shareTooltipTitle(
          getFeatureDisplayName(planFeatureInputFromSuggestion)
        )}
      </div>
    );
  }

  if (isEnterpriseSubscriber) {
    return (
      <div className={styles.container} style={{ maxWidth: 'none' }}>
        {TEXT.enterpriseAddOn()}
      </div>
    );
  }

  const planTitle = featureGatePlanSuggestion?.plan.title;

  if (!planTitle) {
    return <div className={styles.container}>{TEXT.availableWithNoPlan()}</div>;
  }

  const isEnterprisePlan = planTitle === 'Enterprise';

  return (
    <div className={styles.container}>
      {isEnterprisePlan
        ? TEXT.availableWithEnterprise()
        : TEXT.availableWithPlan(planTitle)}
    </div>
  );
}
